import { Button, Empty, Flex, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import type { WorkspaceApp } from 'src/api/facade/types'
import { type AppCredentialRead } from 'src/api/main'
import {
  DateTimeCell,
  ProTableContainer,
  RevokeCredentialModal,
  RotateCredentialModal,
  TableActionColumnTitle
} from 'src/components'
import {
  CREDENTIAL_TOKEN_STATE_KEY,
  CredentialActionsCell,
  CredentialNameCell,
  CredentialStatusCell,
  isCredentialLocked,
  isCredentialRevoked,
  useCredentialRowClick,
  useCredentialsListStyles
} from 'src/features/workspaces/components/credentials'
import { useWorkspaceRoute } from 'src/features/workspaces/pages/WorkspacePageShell'
import { DEFAULT_PER_PAGE, DEFAULT_PER_PAGE_OPTIONS } from 'src/hooks'
import { paths } from 'src/router/paths'
import { useShowApiErrors } from 'src/utils'

import {
  AppCredentialCreateDrawer,
  type CreateFormValues
} from './AppCredentialCreateDrawer'
import {
  AppCredentialEditDrawer,
  type EditFormValues
} from './AppCredentialEditDrawer'
import type { WorkspaceAppDetailApi } from './types'

const { Text } = Typography

type Props = {
  app: WorkspaceApp
  detail: WorkspaceAppDetailApi
}

/** Which of the create/edit drawers is open (if any). */
type DrawerMode =
  | { kind: 'create' }
  | { kind: 'edit'; credential: AppCredentialRead }
  | null

export const AppCredentialsPanel = ({ app, detail }: Props) => {
  const { t } = useTranslation()
  const { styles } = useCredentialsListStyles()
  const navigate = useNavigate()
  const { id: workspaceIdStr } = useWorkspaceRoute()
  const showApiErrors = useShowApiErrors()

  const [drawer, setDrawer] = useState<DrawerMode>(null)
  const [revokeTarget, setRevokeTarget] = useState<AppCredentialRead | null>(
    null
  )
  const [rotateTarget, setRotateTarget] = useState<AppCredentialRead | null>(
    null
  )

  const applicationId = Number(app.id) || 0

  const closeDrawer = () => setDrawer(null)

  const goToDetail = (credentialId: string, token?: string | null) => {
    navigate(
      paths.workspaces.apps.credentialDetail(
        workspaceIdStr,
        app.id,
        credentialId
      ),
      { state: token ? { [CREDENTIAL_TOKEN_STATE_KEY]: token } : undefined }
    )
  }

  const handleCreate = async (values: CreateFormValues) => {
    try {
      const result = await detail.createCredential({
        path: { application_id: applicationId },
        body: {
          name: values.name,
          expires_at: values.expiresAt ? values.expiresAt.toISOString() : null,
          permission_codes: values.permission_codes ?? []
        }
      })
      closeDrawer()
      // Token is returned only once at create — hand it to the detail page
      // via router state; a reload clears it, matching the "show only once"
      // security model.
      goToDetail(result.id, result.token)
    } catch (err) {
      showApiErrors(err)
    }
  }

  const handleEdit = async (values: EditFormValues) => {
    if (drawer?.kind !== 'edit') return
    try {
      await detail.updateCredential({
        path: { application_id: applicationId, id: drawer.credential.id },
        body: {
          name: values.name,
          expires_at: values.expiresAt ? values.expiresAt.toISOString() : null,
          permission_codes: values.permission_codes
        }
      })
      closeDrawer()
    } catch (err) {
      showApiErrors(err)
    }
  }

  const handleConfirmRotate = async (
    expiresAt: string | null,
    revokeOld: boolean
  ) => {
    if (!rotateTarget) return
    const target = rotateTarget
    try {
      const result = await detail.rotateCredential({
        path: { application_id: applicationId, id: target.id },
        body: { expires_at: expiresAt, revoke_old_credential: revokeOld }
      })
      setRotateTarget(null)
      goToDetail(target.id, result.new_credential.token)
    } catch (err) {
      showApiErrors(err)
    }
  }

  const handleConfirmRevoke = async (reason: string | null) => {
    if (!revokeTarget) return
    try {
      await detail.revokeCredential({
        path: { application_id: applicationId, id: revokeTarget.id },
        body: { reason }
      })
      setRevokeTarget(null)
    } catch (err) {
      showApiErrors(err)
    }
  }

  const rowClick = useCredentialRowClick<AppCredentialRead>((cid) =>
    goToDetail(cid)
  )

  // Column order intentionally mirrors the workspace public-access list
  // (Name → Status → Expires → Last used → Actions). Permissions live on the
  // detail page — including them here duplicated data and forced a wider
  // table.
  const columns: ColumnsType<AppCredentialRead> = [
    {
      title: t('workspace.apps.credentials.columns.name'),
      key: 'name',
      fixed: 'left',
      width: 280,
      render: (_, item) => (
        <CredentialNameCell
          name={item.name}
          to={paths.workspaces.apps.credentialDetail(
            workspaceIdStr,
            app.id,
            item.id
          )}
        />
      )
    },
    {
      title: t('workspace.apps.credentials.columns.status'),
      key: 'status',
      width: 160,
      render: (_, item) => (
        <CredentialStatusCell
          item={item}
          statusLabel={t(`workspace.apps.status.${item.status ?? 'active'}`)}
          reasonTip={(reason) =>
            t('workspace.apps.credentials.revokedReason', { reason })
          }
          atTip={(at) => t('workspace.apps.credentials.revokedAt', { at })}
        />
      )
    },
    {
      title: t('workspace.apps.credentials.columns.expires'),
      key: 'expires_at',
      width: 180,
      render: (_, item) => (
        <DateTimeCell
          value={item.expires_at}
          variant="dateTime"
          empty={t('workspace.apps.credentials.noExpiry')}
        />
      )
    },
    {
      title: t('workspace.apps.credentials.columns.lastUsed'),
      key: 'last_used_at',
      width: 180,
      render: (_, item) => (
        <DateTimeCell
          value={item.last_used_at}
          variant="relative"
          empty={t('workspace.apps.credentials.neverUsed')}
        />
      )
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, item) => (
        <CredentialActionsCell
          locked={isCredentialLocked(item.status)}
          revoked={isCredentialRevoked(item.status)}
          onEdit={() => setDrawer({ kind: 'edit', credential: item })}
          onRotate={() => setRotateTarget(item)}
          onRevoke={() => setRevokeTarget(item)}
          rotateLabel={t('workspace.apps.credentials.rotate')}
          revokeLabel={t('workspace.apps.credentials.revoke')}
        />
      )
    }
  ]

  return (
    <Flex vertical gap={14}>
      <ProTableContainer<AppCredentialRead>
        showSearch={false}
        tableIdentifier="app-credentials"
        className={styles.table}
        tableLayout="fixed"
        rowKey="id"
        loading={detail.isLoadingCredentials}
        dataSource={detail.credentials}
        columns={columns}
        onRow={rowClick}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('workspace.apps.credentials.empty')}
                </Text>
              }
            />
          )
        }}
        pagination={{
          defaultPageSize: DEFAULT_PER_PAGE,
          pageSizeOptions: DEFAULT_PER_PAGE_OPTIONS.map(String),
          size: 'small',
          showSizeChanger: true,
          hideOnSinglePage: true
        }}
        scroll={{ x: 900 }}
        leftChildren={
          <span className={styles.count}>
            <strong>{detail.credentials.length}</strong>{' '}
            <Text type="secondary">
              {t('workspace.apps.credentials.toolbar.credentialsLabel')}
            </Text>
          </span>
        }
        rightChildren={
          <Button
            type="primary"
            size="small"
            icon={<Plus size={14} />}
            onClick={() => setDrawer({ kind: 'create' })}
          >
            {t('workspace.apps.credentials.create')}
          </Button>
        }
      />

      <AppCredentialCreateDrawer
        open={drawer?.kind === 'create'}
        app={app}
        submitting={detail.isCreatingCredential}
        onSubmit={handleCreate}
        onClose={closeDrawer}
      />

      <AppCredentialEditDrawer
        open={drawer?.kind === 'edit'}
        app={app}
        credential={drawer?.kind === 'edit' ? drawer.credential : null}
        submitting={detail.isUpdatingCredential}
        onSubmit={handleEdit}
        onClose={closeDrawer}
      />

      <RotateCredentialModal
        open={!!rotateTarget}
        title={t('workspace.apps.credentials.rotateConfirmTitle')}
        description={t('workspace.apps.credentials.rotateConfirmBody')}
        okText={t('workspace.apps.credentials.rotate')}
        loading={detail.isRotatingCredential}
        onConfirm={handleConfirmRotate}
        onCancel={() => setRotateTarget(null)}
      />

      <RevokeCredentialModal
        open={!!revokeTarget}
        title={t('workspace.apps.credentials.revokeConfirmTitle')}
        description={t('workspace.apps.credentials.revokeConfirmBody')}
        okText={t('workspace.apps.credentials.revoke')}
        loading={detail.isRevokingCredential}
        onConfirm={handleConfirmRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </Flex>
  )
}
